import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import {
  Box,
  Spinner,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from "@chakra-ui/react";
import LoginForm from "./LoginForm";
import NavigationArrows from "./NavigationArrows";
import FileUploadModal from "./FileUploadModal";
import QueryListingPage from "./QueryListingPage";
import ChatInterfaceModal from "./ChatInterfaceModal";
import ForecastDashboardModel from "./ForecastDashboardModel";

interface TooltipPosition {
  x: number;
  y: number;
  visible: boolean;
  content: string;
}

interface BoxDimensions {
  width: number;
  height: number;
  depth: number;
}
const Base_apiUrl=process.env.REACT_APP_API_Base_Url;
const ToolTipRotate: React.FC = () => {
  const [tooltipState, setTooltipState] = useState<TooltipPosition>({
    x: 0,
    y: 0,
    visible: false,
    content: "",
  });
  //New Mount code
  const [IndexNumber, SetIndexNumber] = useState(0);

  const mountRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cubeRef = useRef<THREE.Mesh | null>(null);
  const lineRef = useRef<THREE.LineSegments | null>(null);
  const animationFrameId = useRef<number | null>(null);
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster());
  const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2());
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showQueryListingPage, setShowQueryListingPage] = useState(false);
  const [showChatInterface, setShowChatInterface] = useState(false);
  const [showForecastDashboard, setshowForecastDashboard] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false); // Add this state
  const [dimensions, setDimensions] = useState<BoxDimensions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentFaceIndex, setCurrentFaceIndex] = useState(0);
  const [isRotating, setIsRotating] = useState(false);

  const faceIndices = [0, 3, 4, 2, 1, 5];

  const tooltipContents = [
    "Access", // 0 - Access
    "Autonomy", // 1 - MA
    "Communication", // 2 - AM
    "Data Analysis", // 3 - AP
    "AI Models", // 4 - NQ
    "Visualization", // 5 - SW
  ];

  const labels1 = ["Mf", "Ma", "Am", "Ap", "Sv", "Nq"];

  const additionalLabels1 = [
    "Pa,Pr",
    "",
    "Lt",
    "",
    "Pf,Pc,Pl,Ro,Wi,Tl,Si,Ms,Ic",
    "Sn",
  ]; // Additional labels
  const targetRotations = [
    { x: 0, y: 0 }, // Front face (Access - MFA)
    { x: 0, y: Math.PI }, // Back face
    { x: 0, y: -Math.PI / 2 }, // Left face
    { x: 0, y: Math.PI / 2 }, // Right face
    { x: Math.PI / 2, y: 0 }, // Top face
    { x: -Math.PI / 2, y: 0 }, // Bottom face
  ];
  const checkUserLogin = async (): Promise<boolean> => {
    const token = localStorage.getItem("accessToken");
    if (!token) return false;

    try {
      const response = await fetch(
        `${Base_apiUrl}Auth/CheckUserLogin`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) return false;

      const data = await response.json();
      localStorage.setItem("email", data.data);
      return data?.statusCode === 1;
    } catch (error) {
      console.error("Login check failed:", error);
      return false;
    }
  };

  const fetchDimensions = async () => {
    try {
      const response = await fetch(
       `${Base_apiUrl}box/dimensions`
       // 'https://localhost:44319/api/box/dimensions'
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setDimensions({
        width: Number(data.width) || 10,
        height: Number(data.height) || 10,
        depth: Number(data.depth) || 10,
      });
    } catch (err) {
      console.error("Error fetching dimensions:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch dimensions"
      );
      setDimensions({ width: 10, height: 10, depth: 10 });
    }
  };

  useEffect(() => {
    fetchDimensions();

    // Cleanup function for component unmount
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, []);

  // Function to animate cube rotation to target position
  const animateCubeRotation = (targetIndex: number) => {
    if (
      !cubeRef.current ||
      !rendererRef.current ||
      !cameraRef.current ||
      !sceneRef.current
    )
      return;

    setIsRotating(true);
    const targetRotation = targetRotations[targetIndex];

    const animate = () => {
      if (
        !cubeRef.current ||
        !rendererRef.current ||
        !cameraRef.current ||
        !sceneRef.current
      ) {
        setIsRotating(false);
        return;
      }

      const cube = cubeRef.current;
      const renderer = rendererRef.current;
      const camera = cameraRef.current;
      const scene = sceneRef.current;

      // Smooth rotation
      cube.rotation.y = THREE.MathUtils.lerp(
        cube.rotation.y,
        targetRotation.y,
        0.1
      );
      cube.rotation.x = THREE.MathUtils.lerp(
        cube.rotation.x,
        targetRotation.x,
        0.1
      );

      // Update outline
      if (lineRef.current) {
        lineRef.current.rotation.copy(cube.rotation);
      }

      // Check if rotation is complete
      const yDiff = Math.abs(cube.rotation.y - targetRotation.y);
      const xDiff = Math.abs(cube.rotation.x - targetRotation.x);

      if (yDiff < 0.01 && xDiff < 0.01) {
        cube.rotation.y = targetRotation.y;
        cube.rotation.x = targetRotation.x;
        setIsRotating(false);
        renderer.render(scene, camera);
        return;
      }

      renderer.render(scene, camera);
      animationFrameId.current = requestAnimationFrame(animate);
    };

    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }
    animationFrameId.current = requestAnimationFrame(animate);
  };

  // Handle click to rotate cube
  const handleClick = async (event: React.MouseEvent) => {
    if (
      isRotating ||
      !mountRef.current ||
      !cubeRef.current ||
      !cameraRef.current
    )
      return;

    // Get normalized device coordinates
    const rect = mountRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    // Update the raycaster
    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);

    // Check for intersections with the cube
    const intersects = raycasterRef.current.intersectObject(cubeRef.current);

    if (intersects.length > 0) {
      const intersection = intersects[0];
      if (intersection.faceIndex !== undefined) {
        // Get the material index from the face index
        const materialIndex = Math.floor(intersection.faceIndex / 2) % 6;

        // If the Access face (index 0) is clicked, show the login form
        if (materialIndex === 0) {
          setShowLoginForm(true);
          SetIndexNumber(0);
          return;
        }

        // If Data Analysis face (index 3) is clicked, show the file upload form
        if (materialIndex === 3) {
          SetIndexNumber(3);

          // Await the async function and ensure proper type
          const isLoggedIn = await checkUserLogin();
          if (isLoggedIn) {
            setShowFileUpload(true);
          } else {
            alert("Require login");
          }

          return;
        }

        // If Visualization face (index 5) is clicked, redirect to the specified URL
        if (materialIndex === 5) {
          setShowChatInterface(true);
          SetIndexNumber(5);
          return;
        }
        if (materialIndex === 4) {
          setshowForecastDashboard(true);
          SetIndexNumber(4);
          return;
        }
      }
    }
    // For all other cases, rotate the cube as before
    const nextFaceIndex = (currentFaceIndex + 1) % faceIndices.length;
    setCurrentFaceIndex(nextFaceIndex);
    animateCubeRotation(nextFaceIndex);
  };

  // Function to close the login form
  const handleCloseLoginForm = () => {
    setShowLoginForm(false);
  };
  const handleCloseFileUpload = () => {
    setShowFileUpload(false);
  };
  const handleCloseQueryListingPage = () => {
    setShowQueryListingPage(false);
  };
  const handleCloseChatInterface = () => {
    setShowChatInterface(false);
  };
  const handleCloseForecastDashboard = () => {
    setshowForecastDashboard(false);
  };

  // Add this new function inside ToolTipRotate componentvis
  const handleArrowRotation = (direction: "left" | "right" | "up" | "down") => {
    if (isRotating || !cubeRef.current) return;

    const cube = cubeRef.current;
    let targetRotation = { ...targetRotations[currentFaceIndex] };

    switch (direction) {
      case "left":
        targetRotation.y += Math.PI / 2;
        break;
      case "right":
        targetRotation.y -= Math.PI / 2;
        break;
      case "up":
        targetRotation.x += Math.PI / 2;
        break;
      case "down":
        targetRotation.x -= Math.PI / 2;
        break;
    }

    // Update current face index based on rotation
    const nextFaceIndex = calculateNextFaceIndex(direction, currentFaceIndex);
    setCurrentFaceIndex(nextFaceIndex);
    animateCubeRotation(nextFaceIndex);
  };

  // Helper function to calculate next face index
  const calculateNextFaceIndex = (
    direction: "left" | "right" | "up" | "down",
    currentIndex: number
  ) => {
    // This is a simplified version - you might need to adjust the logic based on your needs
    switch (direction) {
      case "left":
        return (currentIndex + 1) % 4;
      case "right":
        return (currentIndex - 1 + 4) % 4;
      case "up":
        return 4;
      case "down":
        return 5;
      default:
        return currentIndex;
    }
  };

  useEffect(() => {
    if (!mountRef.current || !dimensions) return;

    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera setup
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);
    // Move camera more directly in front with less angle
    camera.position.set(0, 0, 20); // Reduced x and y to 0, increased z to 20
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Renderer setup with transparent background
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      premultipliedAlpha: false,
    });
    renderer.setSize(400, 400);
    renderer.setClearColor(0xffffff, 0);
    mountRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create cube with validated dimensions
    const geometry = new THREE.BoxGeometry(
      dimensions.width,
      dimensions.height,
      dimensions.depth
    );

    // Create materials for all faces
    const createMaterials = async () => {
      try {
        const imagePath = "/image/aisc-background.png"; // Update with correct path
        const logoPath = "/image/aisc-logo.png"; // Update with correct path

        // Array of different additional images
        const additionalImages = [
          "/image/access.jpg", // 0 - Access/MFA (Front)
          "/image/autonomy.png", // 1 - MA (Top)
          "/image/communication.png", // 2 - AM (Right)
          "/image/data.png", // 3 - AP (Back)
          "/image/model.png", // 4 - NQ (Left)
          "/image/visual.png", // 5 - SW (Bottom)
        ];

        // Array of labels and additional labels
        const labels = ["Mf", "Ma", "Am", "Ap", "Sv", "Nq"]; // Main labels
        const additionalLabels = [
          "Pa,Pr",
          "",
          "Lt",
          "",
          "Pf,Pc,Pl,Ro,Wi,Tl,Si,Ms,Ic",
          "Sn",
        ]; // Additional labels

        // Generate materials for each face
        const materials = await Promise.all(
          additionalImages.map((additionalImagePath, index) =>
            createCompositeMaterial(
              imagePath,
              logoPath,
              additionalImagePath,
              [labels[index]],
              [additionalLabels[index]],
              index
            )
          )
        );

        return materials;
      } catch (err) {
        console.error("Error creating materials:", err);
        // Fallback to simple colored materials if texture loading fails
        return Array(6).fill(new THREE.MeshBasicMaterial({ color: 0xffffff }));
      }
    };
    // Modified createCompositeMaterial function to track label positions and add click handlers
    const createCompositeMaterial = async (
      imagePath: string,
      logoPath: string,
      additionalImagePath: string,
      labels: string[],
      additionalLabels: string[],
      index: number
    ) => {
      return new Promise<THREE.MeshBasicMaterial>((resolve) => {
        const canvas = document.createElement("canvas");
        canvas.width = 1024;
        canvas.height = 1024;
        const context = canvas.getContext("2d")!;

        // Load the background image
        const bgImage = new Image();
        bgImage.crossOrigin = "anonymous"; // Add this if loading from external URLs
        bgImage.onload = () => {
          context.drawImage(bgImage, 0, 0, canvas.width, canvas.height);

          // Load the logo
          const logoImage = new Image();
          logoImage.crossOrigin = "anonymous";
          logoImage.onload = () => {
            const logoWidth = canvas.width * 0.2; // 20% of the width
            const logoHeight = (logoImage.height / logoImage.width) * logoWidth;
            const logoX = canvas.width - logoWidth - canvas.width * 0.05;
            const logoY = canvas.width * 0.05;
            context.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);

            // Load the additional image
            const additionalImage = new Image();
            additionalImage.crossOrigin = "anonymous";
            additionalImage.onload = () => {
              const additionalWidth = canvas.width * 0.35; // 35% of the width
              const additionalHeight =
                (additionalImage.height / additionalImage.width) *
                additionalWidth;
              const additionalX = canvas.width * 0.35; // Center horizontally
              const additionalY = logoY + logoHeight + canvas.width * 0.05; // Below the logo

              // Add shadow effect to the additional image
              context.shadowColor = "rgba(0, 0, 0, 0.5)";
              context.shadowBlur = 10;
              context.shadowOffsetX = 5;
              context.shadowOffsetY = 5;
              context.drawImage(
                additionalImage,
                additionalX,
                additionalY,
                additionalWidth,
                additionalHeight
              );

              // Reset shadow for text
              context.shadowColor = "transparent";
              context.shadowBlur = 0;
              context.shadowOffsetX = 0;
              context.shadowOffsetY = 0;

              // Add text
              context.fillStyle = "black";
              context.font = "bold 72px Arial"; // Increased from 48px to 72px
              context.textAlign = "center";

              // Add text shadow for better visibility
              context.shadowColor = "rgba(255, 255, 255, 0.8)";
              context.shadowBlur = 4;
              context.shadowOffsetX = 0;
              context.shadowOffsetY = 0;

              // Draw main label with larger size
              context.fillText(
                labels[0],
                canvas.width / 2,
                additionalY + additionalHeight + canvas.width * 0.06
              );

              // Draw additional label with larger size
              if (additionalLabels[0]) {
                context.fillText(
                  additionalLabels[0],
                  canvas.width / 2,
                  additionalY + additionalHeight + canvas.width * 0.12
                );
              }

              // Create material with the composed texture
              const texture = new THREE.CanvasTexture(canvas);
              const material = new THREE.MeshBasicMaterial({
                map: texture,
                transparent: true,
              });

              material.userData = {
                hotspots: [
                  {
                    type: "image",
                    x: additionalX,
                    y: additionalY,
                    width: additionalWidth,
                    height: additionalHeight,
                    tooltip: `${tooltipContents[index]}`,
                    // tooltip: `Image Tooltip: ${tooltipContents[index]}`,
                  },
                  {
                    type: "text",
                    x: canvas.width / 2 - 100,
                    y: additionalY + additionalHeight + canvas.width * 0.01,
                    width: 200,
                    height: 80,
                    tooltip: `${labels1[index]}`,
                    // tooltip: `Text Tooltip: ${labels1[index]}`,
                  },
                  {
                    type: "text",
                    x: canvas.width / 2 - 100,
                    y: additionalY + additionalHeight + canvas.width * 0.1,
                    width: 200,
                    height: 80,
                    tooltip: `${additionalLabels1[index]}`,
                    // tooltip: `Text Tooltip: ${additionalLabels[index]}`,
                  },
                ],
              };

              // const handleMouseMove = (event: MouseEvent) => {
              //   if (!cubeRef.current || !cameraRef.current || !mountRef.current) return;

              //   const rect = mountRef.current.getBoundingClientRect();
              //   mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
              //   mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

              //   raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
              //   const intersects = raycasterRef.current.intersectObject(cubeRef.current);

              //   if (intersects.length > 0) {
              //     const intersection = intersects[0];
              //     const materialIndex = Math.floor(intersection.faceIndex! / 2);
              //     const material = (cubeRef.current.material as THREE.Material[])[materialIndex] as THREE.MeshBasicMaterial;

              //     if (intersection.uv && material.userData?.hotspots) {
              //       const uv = intersection.uv;
              //       const canvasX = uv.x * 1024; // canvas width
              //       const canvasY = (1 - uv.y) * 1024; // flip Y

              //       // Check hotspots
              //       const matched = material.userData.hotspots.find((hotspot: any) =>
              //         canvasX >= hotspot.x &&
              //         canvasX <= hotspot.x + hotspot.width &&
              //         canvasY >= hotspot.y &&
              //         canvasY <= hotspot.y + hotspot.height
              //       );

              //       if (matched) {
              //         setTooltipState({
              //           x: event.clientX,
              //           y: event.clientY,
              //           visible: true,
              //           content: matched.tooltip,
              //         });
              //         return;
              //       }
              //     }
              //   }

              //   // No match
              //   setTooltipState(prev => ({ ...prev, visible: false }));
              // };

              // Update the label positions for tooltips with new size
              // material.userData = {
              //   labelPositions: [
              //     {
              //       // x: canvas.width / 2,
              //       // y: additionalY + additionalHeight + canvas.width * 0.06,
              //       // width: canvas.width * 0.4,
              //       // height: 72, // Update height to match new font size
              //       // text: labels[0],
              //      // tooltip: tooltipContents[Number(labels[0])] || "",
              //     },
              //   ],
              // };

              resolve(material);
            };
            additionalImage.src = additionalImagePath;
            additionalImage.onerror = () => {
              console.error(
                `Failed to load additional image: ${additionalImagePath}`
              );
              // Create fallback material
              resolve(new THREE.MeshBasicMaterial({ color: 0xffffff }));
            };
          };
          logoImage.src = logoPath;
          logoImage.onerror = () => {
            console.error(`Failed to load logo: ${logoPath}`);
            // Continue without logo
            const additionalImage = new Image();
            additionalImage.src = additionalImagePath;
            // ... (similar handling as above)
          };
        };
        bgImage.src = imagePath;
        bgImage.onerror = () => {
          console.error(`Failed to load background: ${imagePath}`);
          // Create fallback material
          resolve(new THREE.MeshBasicMaterial({ color: 0xffffff }));
        };
      });
    };

    // Create cube with materials
    createMaterials().then((materials) => {
      const cube = new THREE.Mesh(geometry, materials);
      cube.rotation.y = 0;
      cube.rotation.x = 0;
      scene.add(cube);
      cubeRef.current = cube;

      // Add black outline
      const edges = new THREE.EdgesGeometry(geometry);
      const line = new THREE.LineSegments(
        edges,
        new THREE.LineBasicMaterial({ color: 0xffffff })
      );
      scene.add(line);
      lineRef.current = line;

      // Initial rendering
      renderer.render(scene, camera);

      // Set up mouse events for tooltips
      setupMouseEvents();

      // Initially animate to the first face (Access/MFA)
      animateCubeRotation(0);
    });

    // Cleanup
    return () => {
      if (mountRef.current && rendererRef.current?.domElement) {
        mountRef.current.removeChild(rendererRef.current.domElement);
      }

      cleanupMouseEvents();

      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }

      rendererRef.current?.dispose();
    };
  }, [dimensions]);

  // Setup mouse events for tooltips
  const setupMouseEvents = () => {
    if (!mountRef.current) return;
    const handleMouseMove = (event: MouseEvent) => {
      if (!cubeRef.current || !cameraRef.current || !mountRef.current) return;

      const rect = mountRef.current.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
      const intersects = raycasterRef.current.intersectObject(cubeRef.current);

      if (intersects.length > 0) {
        const intersection = intersects[0];
        const materialIndex = Math.floor(intersection.faceIndex! / 2);
        const material = (cubeRef.current.material as THREE.Material[])[
          materialIndex
        ] as THREE.MeshBasicMaterial;

        const uv = intersection.uv;
        if (uv && material.userData?.hotspots) {
          const canvasX = uv.x * 1024;
          const canvasY = (1 - uv.y) * 1024;

          const hotspot = material.userData.hotspots.find(
            (hs: any) =>
              canvasX >= hs.x &&
              canvasX <= hs.x + hs.width &&
              canvasY >= hs.y &&
              canvasY <= hs.y + hs.height
          );

          if (hotspot) {
            setTooltipState({
              x: event.clientX,
              y: event.clientY,
              visible: true,
              content: hotspot.tooltip,
            });
            return;
          }
        }
      }

      setTooltipState((prev) => ({ ...prev, visible: false }));
      //};

      // const handleMouseMove = (event: MouseEvent) => {
      //   if (!mountRef.current || !cubeRef.current || !cameraRef.current) return;

      //   const rect = mountRef.current.getBoundingClientRect();
      //   mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      //   mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      //   raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
      //   const intersects = raycasterRef.current.intersectObject(cubeRef.current);

      //   if (intersects.length > 0) {
      //     const intersection = intersects[0];
      //     if (intersection.faceIndex !== undefined) {
      //       const materialIndex = Math.floor(intersection.faceIndex / 2);
      //       const materials = cubeRef.current.material as THREE.Material[];
      //       const material = materials[materialIndex] as THREE.MeshBasicMaterial;

      //       if (material.userData?.labelPositions) {
      //         const point = intersection.point;
      //         const content = tooltipContents[materialIndex];

      //         setTooltipState({
      //           x: event.clientX,
      //           y: event.clientY,
      //           visible: true,
      //           content: content,
      //         });

      //         if (mountRef.current) {
      //           mountRef.current.style.cursor = "pointer";
      //         }
      //         return;
      //       }
      //     }
      //   }

      // setTooltipState((prev) => ({ ...prev, visible: false }));
      // if (mountRef.current) {
      //   mountRef.current.style.cursor = "default";
      // }
    };

    const handleMouseLeave = () => {
      setTooltipState((prev) => ({ ...prev, visible: false }));
    };

    mountRef.current.addEventListener("mousemove", handleMouseMove);
    mountRef.current.addEventListener("mouseleave", handleMouseLeave);

    // Store handlers for cleanup
    mountRef.current.onmousemove = handleMouseMove;
    mountRef.current.onmouseleave = handleMouseLeave;
  };

  // Cleanup mouse events
  const cleanupMouseEvents = () => {
    if (!mountRef.current) return;

    if (mountRef.current.onmousemove) {
      mountRef.current.removeEventListener(
        "mousemove",
        mountRef.current.onmousemove
      );
    }

    if (mountRef.current.onmouseleave) {
      mountRef.current.removeEventListener(
        "mouseleave",
        mountRef.current.onmouseleave
      );
    }
  };

  if (error) {
    return (
      <Box p={4} bg="red.100" borderRadius="md">
        <Text color="red.500" mb={4}>
          {error}
        </Text>
        <Button colorScheme="blue" onClick={fetchDimensions}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!dimensions) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      width="500px"
      height="500px"
      margin="auto"
      marginTop="10px"
    >
      <Box
        ref={mountRef}
        width="400px"
        height="400px"
        borderRadius="md"
        overflow="hidden"
        cursor={isRotating ? "wait" : "pointer"}
        onClick={handleClick}
        position="relative"
        bg="transparent"
        boxShadow="none"
        margin="auto"
      />

      <NavigationArrows
        onRotate={handleArrowRotation}
        isRotating={isRotating}
      />

      {/* Tooltip */}
      {tooltipState.visible && !isRotating && (
        <Box
          position="fixed"
          top={`${tooltipState.y + 10}px`}
          left={`${tooltipState.x + 10}px`}
          bg="gray.800"
          color="white"
          p={2}
          borderRadius="md"
          zIndex={9999}
          maxWidth="250px"
          pointerEvents="none"
          boxShadow="md"
        >
          <Text fontSize="sm">{tooltipState.content}</Text>
        </Box>
      )}

      {showLoginForm && <LoginForm onClose={handleCloseLoginForm} />}
      {showFileUpload && (
        <FileUploadModal
          isOpen={showFileUpload}
          onClose={handleCloseFileUpload}
        />
      )}
      {showQueryListingPage && (
        <QueryListingPage onClose={handleCloseQueryListingPage} />
      )}
      {showChatInterface && (
        <ChatInterfaceModal
          onClose={handleCloseChatInterface}
          isOpen={showChatInterface}
        />
      )}
      {showForecastDashboard && (
        <ForecastDashboardModel
          onClose={handleCloseForecastDashboard}
          isOpen={showForecastDashboard}
        />
      )} 
      <Text fontSize="sm">
         Build version: {process.env.REACT_APP_GIT_COMMIT_HASH}
      </Text>
    </Box>
  );
};

export default ToolTipRotate;
